import React, { useCallback, useEffect, useState } from "react";
import { Eye, Loader2, RefreshCw, Users, Activity, Globe } from "lucide-react";
import api from "@/lib/api";

function fmtDay(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/analytics/visits", { params: { days } });
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, [load]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const maxPv = Math.max(1, ...rows.map((r) => r.pageviews));

  return (
    <section className="mt-10" data-testid="admin-analytics-section">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Eye className="w-5 h-5" style={{ color: "#06B6D4" }} />
          Visitors
        </h2>
        <span
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 rounded font-bold bg-emerald-500/15 text-emerald-400"
          title="Distinct visitors active in the last 5 minutes"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          {totals.online_now ?? 0} online now
        </span>
        <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)]">
          {totals.pageviews_last_hour ?? 0} pv last hour
        </span>
        <div className="ml-auto flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="admin-analytics-window"
            className="bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full px-3 py-1.5 text-xs outline-none cursor-pointer hover:border-[var(--tt-primary)]"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={load}
            data-testid="admin-analytics-refresh"
            className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-[var(--tt-text-muted)] hover:text-white"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Tile Icon={Users} accent="#FF2A6D" label="Unique today" value={totals.unique_today ?? 0} />
        <Tile Icon={Activity} accent="#22C77A" label="Online now" value={totals.online_now ?? 0} />
        <Tile Icon={Eye} accent="#FFD700" label={`PV last ${days}d`} value={(totals.pageviews_window ?? 0).toLocaleString()} />
        <Tile Icon={Globe} accent="#6D4AFF" label="PV last hour" value={totals.pageviews_last_hour ?? 0} />
      </div>

      <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden">
        {loading && !data ? (
          <div className="py-10 flex items-center justify-center text-[var(--tt-text-muted)] text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-[var(--tt-text-muted)] text-sm">No visits recorded yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-analytics-table">
            <thead className="bg-black/40 text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)]">
              <tr>
                <th className="text-left px-4 py-3">Day</th>
                <th className="text-right px-4 py-3">Unique</th>
                <th className="text-right px-4 py-3">Pageviews</th>
                <th className="text-left px-4 py-3 w-1/2">Activity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pct = (r.pageviews / maxPv) * 100;
                return (
                  <tr key={r.day} className="border-t border-[var(--tt-border)] hover:bg-white/[0.02]" data-testid={`admin-analytics-row-${r.day}`}>
                    <td className="px-4 py-2.5 font-mono-tt text-xs">{fmtDay(r.day)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-tt font-semibold">{r.unique_visitors.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right font-mono-tt">{r.pageviews.toLocaleString()}</td>
                    <td className="px-4 py-2.5">
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${pct}%`,
                            background: "linear-gradient(90deg, #06B6D4, #FF2A6D)",
                            boxShadow: "0 0 8px rgba(255,42,109,0.4)",
                          }}
                        />
                      </div>
                      {!!r.top_paths?.length && (
                        <div className="mt-1 text-[10px] text-[var(--tt-text-faint)] font-mono-tt truncate">
                          {r.top_paths.slice(0, 3).map(([p, c]) => `${p} ×${c}`).join("  ·  ")}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-[11px] text-[var(--tt-text-faint)] mt-3 font-mono-tt">
        Anonymous tracking · IPs hashed daily &amp; never stored raw · no cookies. Counts every page view from every visitor, signed-in or not.
      </p>
    </section>
  );
}

function Tile({ Icon, accent, label, value }) {
  return (
    <div className="rounded-2xl bg-[var(--tt-surface)]/70 border border-[var(--tt-border)] backdrop-blur-md px-4 py-3 flex items-center gap-3">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${accent}22`, color: accent }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] font-bold">{label}</div>
        <div className="font-display text-xl font-extrabold tracking-tight">{value}</div>
      </div>
    </div>
  );
}
